import { apiClient } from './apiClient';

export interface Portfolio {
  id: string;
  name: string;
  code: string;
  tenant_id: string;
  description?: string;
  meta?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface PortfoliosResponse {
  items: Portfolio[];
  total: number;
  limit: number;
  offset: number;
}

export interface PaginatedSitesResponse {
  items: PortfolioSite[];
  total: number;
  limit: number;
  offset: number;
}

// Site model as returned from portfolio/site endpoints
export interface PortfolioSite {
  site_id: string;
  tenant_id: string;
  portfolio_id: string;
  site_name: string;
  site_code?: string | null;
  location?: string | null;
  site_capacity_mw?: number | null;
  commissioning_date?: string | null;
  site_type?: string | null;
  timezone?: string | null;
  status?: string | null;
  created_at: string;
  updated_at: string;
  entity_count?: number | null;
  // Additional fields from API
  name?: string;
  id?: string;
  code?: string | null;
  asbuilt_name?: string | null;
  capacity_mw?: number | null;
  address?: string | null;
  meta?: Record<string, any>;
  uploaded_by?: string | null;
  file_size?: number | null;
  uploader?: string | null;
  /** Validation gist for list display (included in list to avoid N+1) */
  validation_result?: SiteValidationSummary | null;
  /** Drawing QA summary (included in list to avoid N+1) */
  drawing_qa?: SiteDrawingQASummary | null;
}

/** Validation gist returned with site list (status + counts only) */
export interface SiteValidationSummary {
  status: 'passed' | 'warnings' | 'errors' | 'not_available';
  error_count: number;
  warning_count: number;
}
export interface CreatePortfolioRequest {
  tenant_id?: string;
  name: string;
  code?: string;
  description?: string;
  meta?: Record<string, any>;
}

export interface UpdatePortfolioRequest {
  name?: string;
  code?: string;
  description?: string;
  meta?: Record<string, any>;
}

// Drawing QA summary (included in site list to avoid N+1)
export interface SiteDrawingQASummary {
  status: 'completed' | 'processing' | 'no_results';
  total_passed?: number;
  total_failed?: number;
}

// Validation Results types
export interface ValidatorDetail {
  stats: Record<string, any> | null;
  valid: boolean;
  errors: string[];
  warnings: string[];
  validation_details?: Record<string, any> | null;
}

export interface ValidationResults {
  valid: boolean;
  errors: string[];
  warnings: string[];
  validators_run: string[];
  validator_details: Record<string, ValidatorDetail>;
}

// Drawing Inconsistency types
export interface DrawingInconsistency {
  edge: string | null;
  node_id: string | null;
  page_used: number;
  description: string;
  conflicting_pages: number[];
}

export interface DrawingInconsistenciesResponse {
  inconsistencies: DrawingInconsistency[];
}

// Site Metadata types
export interface SiteMetadata {
  tilt: number | null;
  azimuth: number | null;
  drawing_date: string | null;
  drawing_type: string | null;
  site_address: string | null;
  site_lat_lon: string | null;
  ac_capacity_kw: number | null;
  dc_capacity_kw: number | null;
  number_of_modules: number | null;
  number_of_strings: number | null;
  modules_per_string: number | null;
  number_of_inverters: number | null;
}

// Artefact types
export interface ArtefactMeta {
  processing_task_id?: string;
  processing_backend?: string;
  processing_enqueued_at?: string;
  [key: string]: any;
}

export interface Artefact {
  id: string;
  created_at: string;
  updated_at: string;
  filename: string;
  content_type: string;
  doc_type: string;
  tags: string[] | null;
  meta: ArtefactMeta | null;
  tenant_id: string;
  site_id: string;
  asset_id: string | null;
  case_id: string | null;
  kind: string;
  title: string;
  file_size: number;
  status: string;
  is_processed: boolean;
  storage_path: string;
  storage_url: string | null;
  chunk_count: number | null;
  processed_at: string | null;
  uploaded_by: string;
  error_message: string | null;
}

export interface ArtefactsResponse {
  items: Artefact[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListPortfolioSitesParams {
  portfolio_id: string;
  uploader_name?: string;
  uploaded_after?: string;
  uploaded_before?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}

export interface SitesPaginationMetadata {
  total: number;
  limit: number;
  next_cursor?: string;
  has_more: boolean;
}

export const portfolioApi = apiClient.injectEndpoints({
  endpoints: (builder) => ({
    // List all portfolios
    listPortfolios: builder.query<PortfoliosResponse, void>({
      query: () => ({
        url: '/api/v1/portfolios/',
        method: 'GET',
      }),
      transformResponse: (response: PortfoliosResponse) => response,
    }),

    // List sites within a portfolio, with optional filters
    listPortfolioSites: builder.query<{ items: PortfolioSite[]; pagination: SitesPaginationMetadata }, ListPortfolioSitesParams>({
      query: ({ portfolio_id, uploader_name, uploaded_after, uploaded_before, search, limit, cursor }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites`,
        method: 'GET',
        params: {
          ...(uploader_name ? { uploader_name } : {}),
          ...(uploaded_after ? { uploaded_after } : {}),
          ...(uploaded_before ? { uploaded_before } : {}),
          ...(search ? { search } : {}),
          ...(limit ? { limit } : {}),
          ...(cursor ? { cursor } : {}),
        },
      }),
      keepUnusedDataFor: 300, // Cache for 5 minutes
      providesTags: (result, error, arg) => [
        { type: 'PortfolioSite', id: arg.portfolio_id },
        'PortfolioSite',
      ],
      transformResponse: (response: any): { items: PortfolioSite[]; pagination: SitesPaginationMetadata } => {
        // Handle both array and object with items property
        let sites: any[] = [];
        if (Array.isArray(response)) {
          sites = response;
        } else if (response?.items && Array.isArray(response.items)) {
          sites = response.items;
        } else {
          sites = [];
        }
        
        // Extract pagination metadata
        const pagination: SitesPaginationMetadata = {
          total: response?.total ?? sites.length,
          limit: response?.limit ?? sites.length,
          next_cursor: response?.next_cursor,
          has_more: response?.has_more ?? false,
        };
        
        // Transform API response to match our interface
        const transformedSites = sites.map((site: any) => ({
          site_id: site.id || site.site_id,
          tenant_id: site.tenant_id,
          portfolio_id: site.portfolio_id,
          site_name: site.name || site.site_name || site.asbuilt_name || 'Unknown Site',
          site_code: site.code || site.site_code || null,
          location: site.location || site.address || null,
          site_capacity_mw: site.capacity_mw || site.site_capacity_mw || null,
          commissioning_date: site.commissioning_date || null,
          site_type: site.site_type || null,
          timezone: site.timezone || null,
          status: site.status || null,
          created_at: site.created_at,
          updated_at: site.updated_at,
          entity_count: site.entity_count || null,
          // Keep original fields for reference
          name: site.name,
          id: site.id || site.site_id,
          code: site.code,
          asbuilt_name: site.asbuilt_name,
          capacity_mw: site.capacity_mw,
          address: site.address,
          meta: site.meta,
          uploaded_by: site.uploaded_by,
          file_size: site.file_size,
          uploader: site.uploader,
          validation_result: site.validation_result ?? null,
          drawing_qa: site.drawing_qa ?? null,
        }));
        
        return {
          items: transformedSites,
          pagination,
        };
      },
    }),

    // Super admin: list sites across an organization (or all sites if organization_id is omitted)
    listAdminSites: builder.query<{ items: PortfolioSite[]; pagination: SitesPaginationMetadata }, { organization_id?: string; uploaded_after?: string; uploaded_before?: string; search?: string; limit?: number; cursor?: string } | void>({
      query: (args) => {
        const organization_id = (args as any)?.organization_id;
        const uploaded_after = (args as any)?.uploaded_after;
        const uploaded_before = (args as any)?.uploaded_before;
        const search = (args as any)?.search;
        const limit = (args as any)?.limit;
        const cursor = (args as any)?.cursor;
        return {
          url: '/api/v1/admin/sites',
          method: 'GET',
          params: {
            ...(organization_id ? { organization_id } : {}),
            ...(uploaded_after ? { uploaded_after } : {}),
            ...(uploaded_before ? { uploaded_before } : {}),
            ...(search ? { search } : {}),
            ...(limit ? { limit } : {}),
            ...(cursor ? { cursor } : {}),
          },
        };
      },
      keepUnusedDataFor: 300, // Cache for 5 minutes
      providesTags: (result, error, arg) => [
        { type: 'AdminSite', id: (arg as any)?.organization_id || 'all' },
        'AdminSite',
      ],
      transformResponse: (response: any): { items: PortfolioSite[]; pagination: SitesPaginationMetadata } => {
        const sites = Array.isArray(response)
          ? response
          : (response?.items && Array.isArray(response.items) ? response.items : []);

        // Extract pagination metadata
        const pagination: SitesPaginationMetadata = {
          total: response?.total ?? sites.length,
          limit: response?.limit ?? sites.length,
          next_cursor: response?.next_cursor,
          has_more: response?.has_more ?? false,
        };

        const transformedSites = sites.map((site: any) => ({
          site_id: site.id || site.site_id,
          tenant_id: site.tenant_id,
          portfolio_id: site.portfolio_id,
          site_name: site.name || site.site_name || site.asbuilt_name || 'Unknown Site',
          site_code: site.code || site.site_code || null,
          location: site.location || site.address || null,
          site_capacity_mw: site.capacity_mw || site.site_capacity_mw || null,
          commissioning_date: site.commissioning_date || null,
          site_type: site.site_type || null,
          timezone: site.timezone || null,
          status: site.status || null,
          created_at: site.created_at,
          updated_at: site.updated_at,
          entity_count: site.entity_count || null,
          // Keep original fields for reference
          name: site.name,
          id: site.id || site.site_id,
          code: site.code,
          asbuilt_name: site.asbuilt_name,
          capacity_mw: site.capacity_mw,
          address: site.address,
          meta: site.meta,
          uploaded_by: site.uploaded_by,
          file_size: site.file_size,
          uploader: site.uploader,
          validation_result: site.validation_result ?? null,
          drawing_qa: site.drawing_qa ?? null,
        }));
        
        return {
          items: transformedSites,
          pagination,
        };
      },
    }),

    // Get portfolio by ID
    getPortfolio: builder.query<Portfolio, string>({
      query: (portfolio_id) => ({
        url: `/api/v1/portfolios/${portfolio_id}`,
        method: 'GET',
      }),
    }),

    // Create portfolio
    createPortfolio: builder.mutation<Portfolio, CreatePortfolioRequest>({
      query: (body) => ({
        url: '/api/v1/portfolios',
        method: 'POST',
        body,
      }),
    }),

    // Update portfolio
    updatePortfolio: builder.mutation<Portfolio, { portfolio_id: string; body: UpdatePortfolioRequest }>({
      query: ({ portfolio_id, body }) => ({
        url: `/api/v1/portfolios/${portfolio_id}`,
        method: 'PATCH',
        body,
      }),
    }),

    // Delete portfolio
    deletePortfolio: builder.mutation<void, string>({
      query: (portfolio_id) => ({
        url: `/api/v1/portfolios/${portfolio_id}`,
        method: 'DELETE',
      }),
    }),

    // Get or create default portfolio
    getDefaultPortfolio: builder.query<Portfolio, void>({
      query: () => ({
        url: '/api/v1/portfolios/default/create-or-get',
        method: 'GET',
      }),
    }),

    // Get a specific site within a portfolio
    getPortfolioSite: builder.query<PortfolioSite, { portfolio_id: string; site_id: string }>({
      query: ({ portfolio_id, site_id }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}`,
        method: 'GET',
      }),
      transformResponse: (site: any): PortfolioSite => {
        // Transform API response to match our interface
        return {
          site_id: site.id || site.site_id,
          tenant_id: site.tenant_id,
          portfolio_id: site.portfolio_id,
          site_name: site.name || site.site_name || site.asbuilt_name || 'Unknown Site',
          site_code: site.code || site.site_code || null,
          location: site.location || site.address || null,
          site_capacity_mw: site.capacity_mw || site.site_capacity_mw || null,
          commissioning_date: site.commissioning_date || null,
          site_type: site.site_type || null,
          timezone: site.timezone || null,
          status: site.status || null,
          created_at: site.created_at,
          updated_at: site.updated_at,
          entity_count: site.entity_count || null,
          // Keep original fields for reference
          name: site.name,
          id: site.id || site.site_id,
          code: site.code,
          asbuilt_name: site.asbuilt_name,
          capacity_mw: site.capacity_mw,
          address: site.address,
          meta: site.meta,
          uploaded_by: site.uploaded_by,
          file_size: site.file_size,
          uploader: site.uploader,
        };
      },
    }),

    // Update a portfolio site
    updatePortfolioSite: builder.mutation<PortfolioSite, { portfolio_id: string; site_id: string; body: { name?: string } }>({
      query: ({ portfolio_id, site_id, body }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'PortfolioSite', id: arg.portfolio_id },
        'PortfolioSite',
        'AdminSite', // Also invalidate admin sites since super admins might be viewing
      ],
      transformResponse: (site: any): PortfolioSite => {
        // Transform API response to match our interface
        return {
          site_id: site.id || site.site_id,
          tenant_id: site.tenant_id,
          portfolio_id: site.portfolio_id,
          site_name: site.name || site.site_name || site.asbuilt_name || 'Unknown Site',
          site_code: site.code || site.site_code || null,
          location: site.location || site.address || null,
          site_capacity_mw: site.capacity_mw || site.site_capacity_mw || null,
          commissioning_date: site.commissioning_date || null,
          site_type: site.site_type || null,
          timezone: site.timezone || null,
          status: site.status || null,
          created_at: site.created_at,
          updated_at: site.updated_at,
          entity_count: site.entity_count || null,
          // Keep original fields for reference
          name: site.name,
          id: site.id || site.site_id,
          code: site.code,
          asbuilt_name: site.asbuilt_name,
          capacity_mw: site.capacity_mw,
          address: site.address,
          meta: site.meta,
          uploaded_by: site.uploaded_by,
          file_size: site.file_size,
          uploader: site.uploader,
        };
      },
    }),

    // Get validation results for a site
    getSiteValidationResults: builder.query<ValidationResults, { portfolio_id: string; site_id: string }>({
      query: ({ portfolio_id, site_id }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/validation-results`,
        method: 'GET',
      }),
      keepUnusedDataFor: 300, // Cache for 5 minutes - validation results rarely change
    }),

    // Get drawing inconsistencies for a site
    getSiteDrawingInconsistencies: builder.query<DrawingInconsistenciesResponse, { portfolio_id: string; site_id: string }>({
      query: ({ portfolio_id, site_id }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/drawing-inconsistencies`,
        method: 'GET',
      }),
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),

    // Get site metadata (extraction metadata)
    getSiteMetadata: builder.query<SiteMetadata, { portfolio_id: string; site_id: string }>({
      query: ({ portfolio_id, site_id }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/site-metadata`,
        method: 'GET',
      }),
    }),

    // Get artefacts for a site
    getSiteArtefacts: builder.query<ArtefactsResponse, { tenant_id: string; site_id: string; limit?: number; offset?: number }>({
      query: ({ tenant_id, site_id, limit = 50, offset = 0 }) => ({
        url: `/api/v1/artefacts?tenant_id=${tenant_id}&site_id=${site_id}&limit=${limit}&offset=${offset}`,
        method: 'GET',
      }),
    }),

    // Get artefact view URL (returns a signed URL for viewing the PDF)
    getArtefactViewUrl: builder.mutation<{ url: string }, { document_id: string; tenant_id: string; expires_in?: number }>({
      query: ({ document_id, tenant_id, expires_in = 3600 }) => ({
        url: `/api/v1/artefacts/${document_id}/view`,
        method: 'POST',
        body: { document_id, tenant_id, expires_in },
      }),
    }),

    getArtefactDownload: builder.mutation<{ url: string }, { document_id: string; tenant_id: string; expires_in?: number }>({
      query: ({ document_id, tenant_id, expires_in = 1800 }) => ({
        url: `/api/v1/artefacts/${document_id}/download`,
        method: 'POST',
        body: { document_id, tenant_id, expires_in },
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useListPortfoliosQuery,
  useGetPortfolioQuery,
  useCreatePortfolioMutation,
  useUpdatePortfolioMutation,
  useDeletePortfolioMutation,
  useGetDefaultPortfolioQuery,
  useLazyGetDefaultPortfolioQuery,
  useListPortfolioSitesQuery,
  useLazyListPortfolioSitesQuery,
  useGetPortfolioSiteQuery,
  useLazyGetPortfolioSiteQuery,
  useUpdatePortfolioSiteMutation,
  useListAdminSitesQuery,
  useGetSiteValidationResultsQuery,
  useLazyGetSiteValidationResultsQuery,
  useGetSiteDrawingInconsistenciesQuery,
  useLazyGetSiteDrawingInconsistenciesQuery,
  useGetSiteMetadataQuery,
  useLazyGetSiteMetadataQuery,
  useGetSiteArtefactsQuery,
  useLazyGetSiteArtefactsQuery,
  useGetArtefactViewUrlMutation,
  useGetArtefactDownloadMutation,
} = portfolioApi;

